import React, {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import api from "../../api/client";
import { trackUxEvent } from "../../analytics/analytics";

export type IssueImpactLevel = "high" | "medium" | "low";
export type IssueType =
  | "approval"
  | "clinical"
  | "contract"
  | "investment"
  | "license"
  | "mna"
  | "funding"
  | "general";

type SortType = "priority" | "latest" | "oldest" | "impactHigh" | "corpName";
type DecisionLabel = "우선 확인" | "추적" | "눌림 대기" | "경계" | "제외";
type HealthStatus = "normal" | "review" | "action" | "risk";

export interface StockIssueUpdate {
  id: number;
  ticker: string;
  corpName: string;
  rceptNo: string;
  reportNm: string;
  rceptDt: string;
  issueType: IssueType;
  issueTitle: string;
  issueSummary?: string;
  impactLevel: IssueImpactLevel;
  sourceUrl?: string;
  theme?: string;
  market?: "KOSPI" | "KOSDAQ";
}

interface IssueUpdatesPageProps {
  apiBaseUrl?: string;
  pageSize?: number;
}

interface IssuePageResponse {
  content: StockIssueUpdate[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

const initialPageResponse: IssuePageResponse = {
  content: [],
  totalElements: 0,
  totalPages: 0,
  size: 20,
  number: 0,
  first: true,
  last: true,
  empty: true,
};


const issueTypeLabelMap: Record<IssueType, string> = {
  approval: "승인/허가",
  clinical: "임상",
  contract: "계약/수주",
  investment: "투자",
  license: "라이선스",
  mna: "M&A",
  funding: "자금조달",
  general: "기타",
};

const impactWeightMap: Record<IssueImpactLevel, number> = {
  high: 3,
  medium: 2,
  low: 1,
};

const issueTypeWeightMap: Record<IssueType, number> = {
  approval: 22,
  clinical: 22,
  contract: 20,
  investment: 15,
  license: 14,
  mna: 14,
  funding: -8,
  general: 2,
};

function useIsMobile(breakpoint = 768) {
  const getIsMobile = useCallback(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= breakpoint;
  }, [breakpoint]);

  const [isMobile, setIsMobile] = useState<boolean>(getIsMobile);

  useEffect(() => {
    const handleResize = () => setIsMobile(getIsMobile());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [getIsMobile]);

  return isMobile;
}

function cleanText(value?: string) {
  return String(value ?? "").replace(/\s+/g, " ").trim();
}

function formatDate(yyyymmdd: string) {
  if (!yyyymmdd || yyyymmdd.length !== 8) return yyyymmdd || "-";
  return `${yyyymmdd.slice(0, 4)}.${yyyymmdd.slice(4, 6)}.${yyyymmdd.slice(6, 8)}`;
}

function formatYmd(date: Date) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}${mm}${dd}`;
}

function shiftLocalDate(days: number) {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + days);
  return date;
}

function getFetchLowerBound(datePreset: string): string | null {
  if (datePreset === "today") return formatYmd(shiftLocalDate(0));
  if (datePreset === "yesterday" || datePreset === "recent") return formatYmd(shiftLocalDate(-1));
  if (datePreset === "7d") return formatYmd(shiftLocalDate(-6));
  if (datePreset === "30d") return formatYmd(shiftLocalDate(-29));
  return null;
}

function getRelativeDayLabel(yyyymmdd: string) {
  if (!yyyymmdd || yyyymmdd.length !== 8) return "";

  const target = new Date(
    `${yyyymmdd.slice(0, 4)}-${yyyymmdd.slice(4, 6)}-${yyyymmdd.slice(6, 8)}T00:00:00`
  );
  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const diffMs = startOfToday.getTime() - target.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays <= 0) return "오늘";
  if (diffDays === 1) return "어제";
  if (diffDays < 30) return `${diffDays}일 전`;
  return "이전";
}

function buildApiUrl(params: {
  apiBaseUrl: string;
  page: number;
  size: number;
  keyword: string;
  impactLevel: string;
  issueType: string;
}) {
  const searchParams = new URLSearchParams();
  searchParams.set("page", String(params.page));
  searchParams.set("size", String(params.size));

  if (params.keyword.trim()) searchParams.set("keyword", params.keyword.trim());
  if (params.impactLevel !== "all") searchParams.set("impactLevel", params.impactLevel);
  if (params.issueType !== "all") searchParams.set("issueType", params.issueType);

  return `${params.apiBaseUrl}?${searchParams.toString()}`;
}

/**
 * 보호된 issues API는 공용 fetch가 아니라 인증 Axios 인스턴스를 사용한다.
 *
 * api 인스턴스의 기본 baseURL은 /api 이지만 apiBaseUrl 기본값도 /api/issues 이므로
 * 이 요청에서는 baseURL을 비워 현재 buildApiUrl()이 만든 경로를 그대로 사용한다.
 * 이렇게 해야 /api/api/issues 중복 경로를 만들지 않으면서 client.ts의
 * Authorization 주입 및 401/419 refresh-retry 로직을 그대로 적용할 수 있다.
 */
async function requestIssuePage(
  url: string,
  signal?: AbortSignal,
): Promise<IssuePageResponse> {
  const response = await api.get<IssuePageResponse>(url, {
    baseURL: "",
    headers: { Accept: "application/json" },
    signal,
  });

  return response.data;
}

const ISSUE_FETCH_CONCURRENCY = 3;

async function fetchIssuePagesWithConcurrency(
  urls: string[],
  signal: AbortSignal,
): Promise<IssuePageResponse[]> {
  if (urls.length === 0) return [];

  const results = new Array<IssuePageResponse>(urls.length);
  let cursor = 0;

  const worker = async () => {
    while (true) {
      if (signal.aborted) throw new DOMException("Aborted", "AbortError");
      const current = cursor;
      cursor += 1;
      if (current >= urls.length) return;
      results[current] = await requestIssuePage(urls[current], signal);
    }
  };

  const workerCount = Math.min(ISSUE_FETCH_CONCURRENCY, urls.length);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return results;
}

function buildIssueHeadline(issue: StockIssueUpdate) {
  const corp = cleanText(issue.corpName);
  const report = cleanText(issue.issueTitle || issue.reportNm);
  if (!corp) return report || "(제목 없음)";
  if (!report) return corp;
  return `${corp} - ${report}`;
}

function getIssueBody(issue: StockIssueUpdate) {
  return cleanText(issue.issueSummary || issue.reportNm || issue.issueTitle || "");
}

function isRiskIssue(issue: StockIssueUpdate) {
  const text = `${issue.reportNm} ${issue.issueTitle} ${issue.issueSummary ?? ""}`;
  if (issue.issueType === "funding") return true;
  return /유상증자|전환사채|CB|BW|소송|제재|불성실|관리종목|상장폐지|감사의견|횡령|배임|리스크|취소|정정/i.test(text);
}

function calculatePriorityScore(issue: StockIssueUpdate) {
  const impact = issue.impactLevel === "high" ? 42 : issue.impactLevel === "medium" ? 24 : 10;
  const issueType = issueTypeWeightMap[issue.issueType] ?? 0;
  const summaryBonus = getIssueBody(issue).length > 20 ? 4 : 0;
  const marketBonus = issue.market ? 2 : 0;
  const riskPenalty = isRiskIssue(issue) ? -10 : 0;

  const relative = getRelativeDayLabel(issue.rceptDt);
  const recency =
    relative === "오늘" ? 12 :
    relative === "어제" ? 8 :
    relative.includes("일 전") ? 4 :
    0;

  return Math.max(0, Math.min(100, impact + issueType + summaryBonus + marketBonus + recency + riskPenalty));
}

function inferDecision(issue: StockIssueUpdate): DecisionLabel {
  const score = calculatePriorityScore(issue);

  if (isRiskIssue(issue) && issue.impactLevel !== "low") return "경계";
  if (score >= 74) return "우선 확인";
  if (score >= 58) return "추적";
  if (score >= 42) return "눌림 대기";
  if (issue.issueType === "general" || issue.impactLevel === "low") return "제외";
  return "추적";
}

function getHealthStatus(issue: StockIssueUpdate): HealthStatus {
  const decision = inferDecision(issue);

  if (decision === "경계") return "risk";
  if (decision === "우선 확인") return "action";
  if (decision === "추적" || decision === "눌림 대기") return "review";
  return "normal";
}

function getHealthLabel(status: HealthStatus) {
  if (status === "action") return "Action";
  if (status === "risk") return "Risk";
  if (status === "review") return "Review";
  return "Normal";
}


function inferWhyImportant(issue: StockIssueUpdate) {
  if (isRiskIssue(issue)) {
    return "재무 부담, 희석 우려, 규제성 리스크로 해석될 수 있어 긍정 재료와 분리해 확인해야 합니다.";
  }
  if (issue.impactLevel === "high" && (issue.issueType === "approval" || issue.issueType === "clinical")) {
    return "승인·임상 계열 고영향 공시는 기대감과 실망감이 모두 빠르게 가격에 반영될 수 있습니다.";
  }
  if (issue.impactLevel === "high" && issue.issueType === "contract") {
    return "계약·수주성 고영향 공시는 실적 기대와 거래대금 증가로 연결될 수 있습니다.";
  }
  if (issue.issueType === "investment" || issue.issueType === "license" || issue.issueType === "mna") {
    return "사업 구조 변화 또는 성장 서사로 연결될 수 있어 후속 기사와 수급 반응 확인이 필요합니다.";
  }
  if (issue.impactLevel === "high") {
    return "고영향 공시로 분류되어 단기 주가 반응 또는 후속 확산 가능성을 먼저 점검할 필요가 있습니다.";
  }
  return "공시 자체보다 이후 시장 반응, 거래대금, 동일 업종 확산 여부를 함께 봐야 의미가 생깁니다.";
}

function inferNextAction(issue: StockIssueUpdate) {
  const decision = inferDecision(issue);

  if (decision === "우선 확인") return "DART 원문 확인 후 당일 거래대금과 관련 뉴스 확산 여부를 먼저 확인하세요.";
  if (decision === "추적") return "후속 공시·뉴스·수급 연결이 이어지는지 관찰하세요.";
  if (decision === "눌림 대기") return "즉시 반응보다 선반영 여부와 눌림 이후 재평가 가능성을 보세요.";
  if (decision === "경계") return "부담 이슈일 수 있으니 방향 확인 전 추격은 피하고 리스크 항목부터 확인하세요.";
  return "핵심 사업 연결성과 실제 가격 반응이 나오는지 확인하세요.";
}

function getDirection(issue: StockIssueUpdate) {
  if (isRiskIssue(issue)) return "Headwind";
  if (issue.impactLevel === "high" && issue.issueType !== "funding") return "Tailwind";
  if (issue.impactLevel === "medium") return "Mixed";
  return "Monitor";
}

function sortItems(items: StockIssueUpdate[], sortBy: SortType): StockIssueUpdate[] {
  const copied = [...items];

  copied.sort((a, b) => {
    if (sortBy === "priority") {
      const scoreDiff = calculatePriorityScore(b) - calculatePriorityScore(a);
      if (scoreDiff !== 0) return scoreDiff;
      if (b.rceptDt !== a.rceptDt) return Number(b.rceptDt) - Number(a.rceptDt);
      return b.id - a.id;
    }

    if (sortBy === "oldest") {
      if (a.rceptDt !== b.rceptDt) return Number(a.rceptDt) - Number(b.rceptDt);
      return a.id - b.id;
    }

    if (sortBy === "impactHigh") {
      const impactDiff = impactWeightMap[b.impactLevel] - impactWeightMap[a.impactLevel];
      if (impactDiff !== 0) return impactDiff;
      if (b.rceptDt !== a.rceptDt) return Number(b.rceptDt) - Number(a.rceptDt);
      return b.id - a.id;
    }

    if (sortBy === "corpName") {
      const corpCompare = a.corpName.localeCompare(b.corpName, "ko");
      if (corpCompare !== 0) return corpCompare;
      if (b.rceptDt !== a.rceptDt) return Number(b.rceptDt) - Number(a.rceptDt);
      return b.id - a.id;
    }

    if (b.rceptDt !== a.rceptDt) return Number(b.rceptDt) - Number(a.rceptDt);
    return b.id - a.id;
  });

  return copied;
}

function applyClientFilters(params: {
  items: StockIssueUpdate[];
  datePreset: string;
  detailImpactLevel: string;
  detailIssueType: string;
}) {
  const { items, datePreset, detailImpactLevel, detailIssueType } = params;

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

  return items.filter((item) => {
    if (detailImpactLevel !== "all" && item.impactLevel !== detailImpactLevel) return false;
    if (detailIssueType !== "all" && item.issueType !== detailIssueType) return false;

    if (datePreset === "all") return true;
    if (!item.rceptDt || item.rceptDt.length !== 8) return false;

    const targetTime = new Date(
      `${item.rceptDt.slice(0, 4)}-${item.rceptDt.slice(4, 6)}-${item.rceptDt.slice(6, 8)}T00:00:00`
    ).getTime();
    const diffDays = Math.floor((todayStart - targetTime) / (1000 * 60 * 60 * 24));

    if (datePreset === "recent") return diffDays >= 0 && diffDays <= 1;
    if (datePreset === "today") return diffDays === 0;
    if (datePreset === "yesterday") return diffDays === 1;
    if (datePreset === "7d") return diffDays >= 0 && diffDays < 7;
    if (datePreset === "30d") return diffDays >= 0 && diffDays < 30;

    return true;
  });
}

function getStatusCounts(items: StockIssueUpdate[]) {
  return items.reduce(
    (acc, item) => {
      const status = getHealthStatus(item);
      acc[status] += 1;
      return acc;
    },
    { normal: 0, review: 0, action: 0, risk: 0 }
  );
}


function getHeroSummary(items: StockIssueUpdate[]) {
  const first = sortItems(items, "priority")[0];
  const highCount = items.filter((item) => item.impactLevel === "high").length;
  const riskCount = items.filter(isRiskIssue).length;

  if (!first) {
    return "현재 조건에서 표시할 공시 이벤트가 없습니다. 검색어나 필터를 조정해 다시 확인하세요.";
  }

  if (riskCount >= 2) {
    return "부담성 공시가 여러 건 감지되어 긍정 이벤트와 리스크 이벤트를 분리해서 확인해야 합니다.";
  }

  if (highCount >= 3) {
    return "고영향 공시 비중이 높습니다. 공시 원문, 거래대금, 후속 뉴스 확산 여부를 우선 확인해야 합니다.";
  }

  return `${buildIssueHeadline(first)} 중심으로 ${items.length}건의 이벤트가 감지되었습니다. 우선순위와 공시 근거를 기준으로 선별 확인하세요.`;
}

function getTopEvents(items: StockIssueUpdate[], limit: number) {
  return sortItems(items, "priority").slice(0, limit);
}

function getRelatedAssets(issue: StockIssueUpdate) {
  return [
    {
      ticker: issue.ticker || "-",
      name: issue.corpName || "종목명 없음",
      description: `${issue.market || "시장 미상"} · ${issueTypeLabelMap[issue.issueType] || issue.issueType}`,
    },
  ];
}

function Pagination({
  page,
  totalPages,
  onChange,
  isMobile,
}: {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
  isMobile: boolean;
}) {
  if (totalPages <= 1) return null;

  const safePage = Math.min(Math.max(page, 0), totalPages - 1);
  const currentPageNumber = safePage + 1;

  if (isMobile) {
    return (
      <nav style={styles.mobilePagination} aria-label="이벤트 페이지 이동">
        <button
          type="button"
          style={{ ...styles.mobilePageNavButton, opacity: safePage === 0 ? 0.42 : 1 }}
          onClick={() => onChange(Math.max(safePage - 1, 0))}
          disabled={safePage === 0}
        >
          ← 이전
        </button>
        <div style={styles.mobilePageIndicator}>
          <strong>{currentPageNumber}</strong>
          <span>/ {totalPages}</span>
        </div>
        <button
          type="button"
          style={{ ...styles.mobilePageNavButton, opacity: safePage >= totalPages - 1 ? 0.42 : 1 }}
          onClick={() => onChange(Math.min(safePage + 1, totalPages - 1))}
          disabled={safePage >= totalPages - 1}
        >
          다음 →
        </button>
      </nav>
    );
  }

  const PAGE_BLOCK_SIZE = 5;
  const currentBlockIndex = Math.floor(safePage / PAGE_BLOCK_SIZE);
  const blockStartPage = currentBlockIndex * PAGE_BLOCK_SIZE + 1;
  const blockEndPage = Math.min(blockStartPage + PAGE_BLOCK_SIZE - 1, totalPages);

  const pages: number[] = [];
  for (let p = blockStartPage; p <= blockEndPage; p += 1) pages.push(p);

  const hasPrevBlock = blockStartPage > 1;
  const hasNextBlock = blockEndPage < totalPages;
  const prevBlockPage = Math.max(blockStartPage - PAGE_BLOCK_SIZE - 1, 0);
  const nextBlockPage = blockEndPage;

  return (
    <div style={styles.paginationWrap}>
      <button
        type="button"
        style={styles.secondaryButton}
        onClick={() => onChange(Math.max(safePage - 1, 0))}
        disabled={safePage === 0}
      >
        이전
      </button>

      <div style={styles.paginationNumbers}>
        {hasPrevBlock && (
          <button type="button" onClick={() => onChange(prevBlockPage)} style={styles.pageButton}>
            ...
          </button>
        )}

        {pages.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p - 1)}
            style={p === currentPageNumber ? { ...styles.pageButton, ...styles.pageButtonActive } : styles.pageButton}
          >
            {p}
          </button>
        ))}

        {hasNextBlock && (
          <button type="button" onClick={() => onChange(nextBlockPage)} style={styles.pageButton}>
            ...
          </button>
        )}
      </div>

      <button
        type="button"
        style={styles.secondaryButton}
        onClick={() => onChange(Math.min(safePage + 1, totalPages - 1))}
        disabled={safePage >= totalPages - 1}
      >
        다음
      </button>
    </div>
  );
}

function StatusBadge({ status }: { status: HealthStatus }) {
  return <span style={{ ...styles.statusBadge, ...statusStyle[status] }}>{getHealthLabel(status)}</span>;
}

function MetricCard({
  label,
  value,
  sub,
  tone = "default",
}: {
  label: string;
  value: string | number;
  sub: string;
  tone?: "default" | "good" | "warn" | "review";
}) {
  return (
    <div style={styles.metricCard}>
      <div style={styles.metricLabel}>{label}</div>
      <div
        style={{
          ...styles.metricValue,
          color:
            tone === "good" ? UI.green :
            tone === "warn" ? UI.red :
            tone === "review" ? UI.amber :
            UI.text,
        }}
      >
        {value}
      </div>
      <div style={styles.metricSub}>{sub}</div>
    </div>
  );
}

function SearchFilterBar({
  isMobile,
  searchInput,
  tab,
  sortBy,
  datePreset,
  detailImpactLevel,
  detailIssueType,
  onSearchInputChange,
  onSearchSubmit,
  onSearchKeyDown,
  onTabChange,
  onSortChange,
  onDatePresetChange,
  onImpactChange,
  onIssueTypeChange,
  onReset,
}: {
  isMobile: boolean;
  searchInput: string;
  tab: string;
  sortBy: SortType;
  datePreset: string;
  detailImpactLevel: string;
  detailIssueType: string;
  onSearchInputChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onSearchSubmit: () => void;
  onSearchKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  onTabChange: (value: string) => void;
  onSortChange: (value: SortType) => void;
  onDatePresetChange: (value: string) => void;
  onImpactChange: (value: string) => void;
  onIssueTypeChange: (value: string) => void;
  onReset: () => void;
}) {
  const [open, setOpen] = useState(false);

  const categoryTabs = [
    { value: "all", label: "전체" },
    { value: "hot", label: "고영향" },
    { value: "approval", label: "승인/임상" },
    { value: "contract", label: "계약/수주" },
    { value: "funding", label: "자금조달/리스크" },
  ];

  if (isMobile) {
    return (
      <section style={{ ...styles.filterPanel, ...styles.filterPanelMobile }}>
        <div style={styles.mobileSearchBox}>
          <input
            value={searchInput}
            onChange={onSearchInputChange}
            onKeyDown={onSearchKeyDown}
            placeholder="종목명 · 공시명 · 이슈 검색"
            style={{ ...styles.searchInput, ...styles.searchInputMobile }}
          />
          <button type="button" onClick={onSearchSubmit} style={{ ...styles.primaryButton, ...styles.mobileSearchButton }}>
            검색
          </button>
        </div>

        <div style={styles.mobileFilterActions}>
          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            style={{ ...styles.secondaryButton, ...styles.mobileFilterToggle }}
          >
            {open ? "상세 필터 닫기" : "상세 필터"}
          </button>
          <button type="button" onClick={onReset} style={styles.mobileResetButton}>
            초기화
          </button>
        </div>

        <div style={styles.mobileTabScroller}>
          {categoryTabs.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => onTabChange(item.value)}
              style={tab === item.value ? { ...styles.tabButton, ...styles.tabButtonActive, ...styles.mobileTabButton } : { ...styles.tabButton, ...styles.mobileTabButton }}
            >
              {item.label}
            </button>
          ))}
        </div>

        {open && (
          <div style={{ ...styles.filterGrid, ...styles.filterGridMobile }}>
            <label style={styles.selectField}>
              <span style={styles.selectLabel}>정렬</span>
              <select value={sortBy} onChange={(e) => onSortChange(e.target.value as SortType)} style={styles.select}>
                <option value="priority">우선순위순</option>
                <option value="latest">최신순</option>
                <option value="oldest">오래된순</option>
                <option value="impactHigh">영향도 높은순</option>
                <option value="corpName">종목명순</option>
              </select>
            </label>

            <label style={styles.selectField}>
              <span style={styles.selectLabel}>기간</span>
              <select value={datePreset} onChange={(e) => onDatePresetChange(e.target.value)} style={styles.select}>
                <option value="recent">오늘·어제</option>
                <option value="all">전체</option>
                <option value="today">오늘</option>
                <option value="yesterday">어제</option>
                <option value="7d">최근 7일</option>
                <option value="30d">최근 30일</option>
              </select>
            </label>

            <label style={styles.selectField}>
              <span style={styles.selectLabel}>영향도</span>
              <select value={detailImpactLevel} onChange={(e) => onImpactChange(e.target.value)} style={styles.select}>
                <option value="all">전체</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </label>

            <label style={styles.selectField}>
              <span style={styles.selectLabel}>이슈 유형</span>
              <select value={detailIssueType} onChange={(e) => onIssueTypeChange(e.target.value)} style={styles.select}>
                <option value="all">전체</option>
                <option value="approval">승인/허가</option>
                <option value="clinical">임상</option>
                <option value="contract">계약/수주</option>
                <option value="investment">투자</option>
                <option value="license">라이선스</option>
                <option value="mna">M&A</option>
                <option value="funding">자금조달</option>
                <option value="general">기타</option>
              </select>
            </label>
          </div>
        )}
      </section>
    );
  }

  return (
    <section style={styles.filterPanel}>
      <div style={styles.searchLine}>
        <input
          value={searchInput}
          onChange={onSearchInputChange}
          onKeyDown={onSearchKeyDown}
          placeholder="종목, 공시, 이슈 검색"
          style={styles.searchInput}
        />
        <button type="button" onClick={onSearchSubmit} style={styles.primaryButton}>
          검색
        </button>
        <button type="button" onClick={onReset} style={styles.secondaryButton}>
          초기화
        </button>
      </div>

      <div style={styles.tabRow}>
        {categoryTabs.map((item) => (
          <button
            key={item.value}
            type="button"
            onClick={() => onTabChange(item.value)}
            style={tab === item.value ? { ...styles.tabButton, ...styles.tabButtonActive } : styles.tabButton}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div style={{ ...styles.filterGrid, gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}>
        <label style={styles.selectField}>
          <span style={styles.selectLabel}>정렬</span>
          <select value={sortBy} onChange={(e) => onSortChange(e.target.value as SortType)} style={styles.select}>
            <option value="priority">우선순위순</option>
            <option value="latest">최신순</option>
            <option value="oldest">오래된순</option>
            <option value="impactHigh">영향도 높은순</option>
            <option value="corpName">종목명순</option>
          </select>
        </label>

        <label style={styles.selectField}>
          <span style={styles.selectLabel}>기간</span>
          <select value={datePreset} onChange={(e) => onDatePresetChange(e.target.value)} style={styles.select}>
            <option value="recent">오늘·어제</option>
            <option value="all">전체</option>
            <option value="today">오늘</option>
            <option value="yesterday">어제</option>
            <option value="7d">최근 7일</option>
            <option value="30d">최근 30일</option>
          </select>
        </label>

        <label style={styles.selectField}>
          <span style={styles.selectLabel}>영향도</span>
          <select value={detailImpactLevel} onChange={(e) => onImpactChange(e.target.value)} style={styles.select}>
            <option value="all">전체</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </label>

        <label style={styles.selectField}>
          <span style={styles.selectLabel}>이슈 유형</span>
          <select value={detailIssueType} onChange={(e) => onIssueTypeChange(e.target.value)} style={styles.select}>
            <option value="all">전체</option>
            <option value="approval">승인/허가</option>
            <option value="clinical">임상</option>
            <option value="contract">계약/수주</option>
            <option value="investment">투자</option>
            <option value="license">라이선스</option>
            <option value="mna">M&A</option>
            <option value="funding">자금조달</option>
            <option value="general">기타</option>
          </select>
        </label>
      </div>
    </section>
  );
}

function MarketPerspectiveCard({
  isMobile,
  summary,
  stats,
  statusCounts,
}: {
  isMobile: boolean;
  summary: string;
  stats: {
    serverTotal: number;
    currentCount: number;
    highCount: number;
    riskCount: number;
    latestDate: string;
    avgPriority: number;
  };
  statusCounts: Record<HealthStatus, number>;
}) {
  if (isMobile) {
    return (
      <section style={styles.mobileSnapshotCard}>
        <div style={styles.mobileSnapshotHeader}>
          <div>
            <div style={styles.mobileSnapshotEyebrow}>오늘의 공시 상태</div>
            <div style={styles.mobileSnapshotTitle}>{getLeadingDirectionLabel(statusCounts)}</div>
          </div>
          <div style={styles.mobileSnapshotDate}>{stats.latestDate}</div>
        </div>

        <div style={styles.mobileStatusScroller}>
          <span style={{ ...styles.miniPill, ...statusStyle.action }}>{statusCounts.action} Action</span>
          <span style={{ ...styles.miniPill, ...statusStyle.risk }}>{statusCounts.risk} Risk</span>
          <span style={{ ...styles.miniPill, ...statusStyle.review }}>{statusCounts.review} Review</span>
          <span style={{ ...styles.miniPill, ...statusStyle.normal }}>{statusCounts.normal} Normal</span>
        </div>

        <p style={styles.mobileSnapshotSummary}>{summary}</p>

        <div style={styles.mobileSnapshotGrid}>
          <div style={styles.mobileSnapshotMetric}>
            <span>현재 이벤트</span>
            <strong>{stats.currentCount}</strong>
          </div>
          <div style={styles.mobileSnapshotMetric}>
            <span>고영향</span>
            <strong style={{ color: UI.green }}>{stats.highCount}</strong>
          </div>
          <div style={styles.mobileSnapshotMetric}>
            <span>리스크</span>
            <strong style={{ color: stats.riskCount > 0 ? UI.red : UI.text }}>{stats.riskCount}</strong>
          </div>
          <div style={styles.mobileSnapshotMetric}>
            <span>평균 우선순위</span>
            <strong style={{ color: UI.amber }}>{stats.avgPriority}</strong>
          </div>
        </div>

        <div style={styles.mobileSnapshotFoot}>서버 전체 {stats.serverTotal}건 · 현재 필터 기준</div>
      </section>
    );
  }

  return (
    <section style={styles.marketCard}>
      <div style={styles.marketHeader}>
        <div>
          <div style={styles.cardTitle}>Today&apos;s Event Intelligence</div>
          <div style={styles.cardSub}>{stats.latestDate}</div>
        </div>
        <div style={styles.statusSummary}>
          <span style={{ ...styles.miniPill, ...statusStyle.normal }}>{statusCounts.normal} Normal</span>
          <span style={{ ...styles.miniPill, ...statusStyle.review }}>{statusCounts.review} Review</span>
          <span style={{ ...styles.miniPill, ...statusStyle.action }}>{statusCounts.action} Action</span>
          <span style={{ ...styles.miniPill, ...statusStyle.risk }}>{statusCounts.risk} Risk</span>
        </div>
      </div>

      <p style={styles.marketSummary}>{summary}</p>

      <div style={{ ...styles.marketGrid, gridTemplateColumns: "1fr 1fr" }}>
        <div>
          <div style={styles.sectionEyebrow}>Event Distribution</div>
          <div style={styles.simpleRadarBox}>
            <div style={styles.radarLine}>
              <span>전체 이벤트</span>
              <strong>{stats.currentCount}</strong>
            </div>
            <div style={styles.progressTrack}>
              <div style={{ ...styles.progressFill, width: `${Math.min(100, stats.currentCount * 3)}%`, background: UI.blue }} />
            </div>

            <div style={styles.radarLine}>
              <span>고영향 이벤트</span>
              <strong>{stats.highCount}</strong>
            </div>
            <div style={styles.progressTrack}>
              <div style={{ ...styles.progressFill, width: `${stats.currentCount ? Math.min(100, (stats.highCount / stats.currentCount) * 100) : 0}%`, background: UI.green }} />
            </div>

            <div style={styles.radarLine}>
              <span>리스크성 이벤트</span>
              <strong>{stats.riskCount}</strong>
            </div>
            <div style={styles.progressTrack}>
              <div style={{ ...styles.progressFill, width: `${stats.currentCount ? Math.min(100, (stats.riskCount / stats.currentCount) * 100) : 0}%`, background: UI.red }} />
            </div>
          </div>
        </div>

        <div>
          <div style={styles.sectionEyebrow}>Market Directionality</div>
          <div style={styles.directionBox}>
            <div style={styles.directionTitle}>{getLeadingDirectionLabel(statusCounts)}</div>
            <div style={styles.directionSub}>
              현재 화면은 테마명이 아니라 공시 영향도, 리스크 여부, 우선순위 점수 기준으로 재정렬됩니다.
            </div>

            <div style={styles.directionStats}>
              <div style={styles.directionStat}>
                <span>평균 우선순위</span>
                <strong>{stats.avgPriority}</strong>
              </div>
              <div style={styles.directionStat}>
                <span>서버 전체</span>
                <strong>{stats.serverTotal}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function getLeadingDirectionLabel(statusCounts: Record<HealthStatus, number>) {
  if (statusCounts.risk > statusCounts.action && statusCounts.risk > 0) return "Risk Control";
  if (statusCounts.action > 0) return "Event Driven";
  if (statusCounts.review > 0) return "Selective Review";
  return "Monitoring";
}

function FocusEventsSection({
  events,
  isMobile,
  onSelect,
}: {
  events: StockIssueUpdate[];
  isMobile: boolean;
  onSelect: (issue: StockIssueUpdate) => void;
}) {
  if (events.length === 0) return null;

  return (
    <section style={styles.focusSection}>
      <div style={{ ...styles.sectionHeader, ...(isMobile ? styles.sectionHeaderMobile : {}) }}>
        <div>
          <h2 style={{ ...styles.sectionTitle, ...(isMobile ? styles.sectionTitleMobile : {}) }}>우선 확인 이벤트</h2>
          <p style={{ ...styles.sectionDesc, ...(isMobile ? styles.sectionDescMobile : {}) }}>
            영향도와 우선순위를 기준으로 먼저 확인할 공시입니다.
          </p>
        </div>
      </div>

      <div style={styles.focusGrid}>
        {events.map((issue) => {
          const status = getHealthStatus(issue);
          const score = calculatePriorityScore(issue);
          const direction = getDirection(issue);

          if (isMobile) {
            return (
              <button
                key={`focus-${issue.id}`}
                type="button"
                style={styles.mobileFocusCard}
                onClick={() => onSelect(issue)}
              >
                <div style={styles.mobileFocusTop}>
                  <div style={styles.mobileFocusCorpLine}>
                    <span style={styles.tickerChip}>{issue.ticker || "-"}</span>
                    <strong>{issue.corpName || "종목명 없음"}</strong>
                  </div>
                  <StatusBadge status={status} />
                </div>

                <div style={styles.mobileFocusTitle}>{cleanText(issue.issueTitle || issue.reportNm) || "공시 이벤트"}</div>
                <div style={styles.mobileFocusMeta}>
                  {issueTypeLabelMap[issue.issueType]} · {formatDate(issue.rceptDt)} · {getRelativeDayLabel(issue.rceptDt)}
                </div>

                <div style={styles.mobileFocusBottom}>
                  <span>{direction}</span>
                  <strong>{score}점</strong>
                  <em>상세 보기 →</em>
                </div>
              </button>
            );
          }

          return (
            <article key={`focus-${issue.id}`} style={styles.focusCard} onClick={() => onSelect(issue)}>
              <div style={styles.focusTop}>
                <div>
                  <h3 style={styles.focusTitle}>{cleanText(issue.issueTitle || issue.reportNm) || "공시 이벤트"}</h3>
                  <div style={styles.focusMeta}>
                    {issueTypeLabelMap[issue.issueType]} · {formatDate(issue.rceptDt)}
                  </div>
                </div>
                <StatusBadge status={status} />
              </div>

              <p style={styles.focusSummary}>{inferWhyImportant(issue)}</p>

              <div style={styles.impactRow}>
                <span>Impact</span>
                <strong>{direction}</strong>
              </div>
              <div style={styles.progressTrack}>
                <div
                  style={{
                    ...styles.progressFill,
                    width: `${score}%`,
                    background: status === "risk" ? UI.red : status === "action" ? UI.green : status === "review" ? UI.amber : UI.slate,
                  }}
                />
              </div>

              <div style={styles.relatedBox}>
                <div style={styles.relatedTitle}>Related Asset</div>
                <div style={styles.relatedLine}>
                  <span style={styles.tickerChip}>{issue.ticker || "-"}</span>
                  <span>{issue.corpName || "종목명 없음"}</span>
                </div>
              </div>

              <button type="button" style={styles.cardLinkButton}>
                상세 근거 확인 →
              </button>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function AssetHealthList({
  items,
  isMobile,
  expandedId,
  onToggle,
  onSelect,
}: {
  items: StockIssueUpdate[];
  isMobile: boolean;
  expandedId: number | null;
  onToggle: (id: number) => void;
  onSelect: (issue: StockIssueUpdate) => void;
}) {
  return (
    <section style={{ ...styles.assetPanel, ...(isMobile ? styles.assetPanelMobile : {}) }}>
      <div style={{ ...styles.assetHeader, ...(isMobile ? styles.assetHeaderMobile : {}) }}>
        <div>
          <h2 style={{ ...styles.assetTitle, ...(isMobile ? styles.assetTitleMobile : {}) }}>Research Timeline</h2>
          {isMobile && <div style={styles.assetHeaderSub}>항목을 눌러 핵심 분석을 펼칠 수 있습니다.</div>}
        </div>
        {isMobile && <span style={styles.assetCountBadge}>{items.length}건</span>}
      </div>

      <div style={styles.assetList}>
        {items.map((issue) => {
          const status = getHealthStatus(issue);
          const expanded = expandedId === issue.id;
          const score = calculatePriorityScore(issue);
          const body = getIssueBody(issue);

          return (
            <article
              key={issue.id}
              style={{
                ...styles.assetRow,
                ...(isMobile ? styles.assetRowMobile : {}),
                borderColor: statusBorderColor[status],
              }}
            >
              <button
                type="button"
                style={isMobile ? styles.assetRowMainMobile : styles.assetRowMain}
                onClick={() => onToggle(issue.id)}
                aria-expanded={expanded}
              >
                {isMobile ? (
                  <>
                    <div style={styles.mobileAssetTop}>
                      <div style={styles.mobileAssetIdentity}>
                        <span style={styles.tickerLarge}>{issue.ticker || "-"}</span>
                        <div style={styles.mobileAssetCorp}>{issue.corpName || "종목명 없음"}</div>
                      </div>
                      <div style={styles.mobileAssetScoreBox}>
                        <strong>{score}</strong>
                        <span>우선순위</span>
                      </div>
                    </div>

                    <div style={styles.mobileAssetTitle}>{cleanText(issue.issueTitle || issue.reportNm) || "공시 이벤트"}</div>

                    <div style={styles.mobileAssetMetaRow}>
                      <StatusBadge status={status} />
                      <span style={styles.mobileMetaPill}>{issueTypeLabelMap[issue.issueType]}</span>
                      <span style={styles.mobileMetaPill}>{getRelativeDayLabel(issue.rceptDt) || formatDate(issue.rceptDt)}</span>
                    </div>

                    <div style={styles.mobileAssetFooter}>
                      <span>{getDirection(issue)}</span>
                      <span>{expanded ? "접기 ↑" : "핵심 분석 펼치기 ↓"}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ ...styles.assetLeft, minWidth: 260 }}>
                      <div style={styles.assetNameLine}>
                        <span style={styles.tickerLarge}>{issue.ticker || "-"}</span>
                        <StatusBadge status={status} />
                      </div>
                      <div style={styles.assetCorp}>{issue.corpName || "종목명 없음"}</div>
                    </div>

                    <div style={styles.assetMiddle}>
                      <div style={styles.assetEvent}>{cleanText(issue.issueTitle || issue.reportNm) || "공시 이벤트"}</div>
                      <div style={styles.assetSub}>
                        {issueTypeLabelMap[issue.issueType]} · {formatDate(issue.rceptDt)} · {getRelativeDayLabel(issue.rceptDt)}
                      </div>
                    </div>

                    <div style={styles.assetRight}>
                      <div style={styles.scoreValue}>{score}</div>
                      <div style={styles.scoreSub}>priority</div>
                    </div>

                    <div style={styles.chevron}>{expanded ? "⌃" : "⌄"}</div>
                  </>
                )}
              </button>

              {expanded && (
                <div style={{ ...styles.assetExpanded, ...(isMobile ? styles.assetExpandedMobile : {}) }}>
                  <div style={{ ...styles.expandedGrid, gridTemplateColumns: isMobile ? "1fr" : "1.2fr 1fr 1fr" }}>
                    <div style={isMobile ? styles.mobileAnalysisBlock : undefined}>
                      <div style={styles.expandedLabel}>핵심 내용</div>
                      <p style={styles.expandedText}>{body || inferWhyImportant(issue)}</p>
                    </div>

                    <div style={isMobile ? styles.mobileAnalysisBlock : undefined}>
                      <div style={styles.expandedLabel}>왜 중요한가</div>
                      <p style={styles.expandedText}>{inferWhyImportant(issue)}</p>
                    </div>

                    <div style={isMobile ? styles.mobileAnalysisBlock : undefined}>
                      <div style={styles.expandedLabel}>다음 확인</div>
                      <p style={styles.expandedText}>{inferNextAction(issue)}</p>
                    </div>
                  </div>

                  <div style={{ ...styles.expandedActionRow, ...(isMobile ? styles.expandedActionRowMobile : {}) }}>
                    <button
                      type="button"
                      style={{ ...styles.secondaryButton, ...(isMobile ? styles.mobileFullButton : {}) }}
                      onClick={() => onSelect(issue)}
                    >
                      상세 분석 보기
                    </button>
                    {issue.sourceUrl ? (
                      <a
                        href={issue.sourceUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() =>
                          trackUxEvent("issue_external_link_click", {
                            source: "issue-list-expanded",
                            destination: "dart",
                            issue_id: issue.id,
                            ticker: issue.ticker || undefined,
                            rcept_no: issue.rceptNo || undefined,
                          })
                        }
                        style={{ ...styles.primaryButton, ...(isMobile ? styles.mobileFullButton : {}) }}
                      >
                        DART 원문 보기
                      </a>
                    ) : null}
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}

function EventDetailDrawer({
  issue,
  onClose,
  isMobile,
}: {
  issue: StockIssueUpdate | null;
  onClose: () => void;
  isMobile: boolean;
}) {
  useEffect(() => {
    if (!issue || typeof document === "undefined") return;

    const previousOverflow = document.body.style.overflow;
    const previousOverscroll = document.body.style.overscrollBehavior;
    document.body.style.overflow = "hidden";
    document.body.style.overscrollBehavior = "none";

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.overscrollBehavior = previousOverscroll;
    };
  }, [issue]);

  if (!issue) return null;

  const status = getHealthStatus(issue);
  const score = calculatePriorityScore(issue);
  const relatedAssets = getRelatedAssets(issue);

  return (
    <>
      <div style={{ ...styles.drawerBackdrop, ...(isMobile ? styles.drawerBackdropMobile : {}) }} onClick={onClose} />
      <aside
        style={{ ...styles.drawer, ...(isMobile ? styles.drawerMobile : {}) }}
        role="dialog"
        aria-modal="true"
        aria-label="이벤트 상세 분석"
      >
        <div style={{ ...styles.drawerHeader, ...(isMobile ? styles.drawerHeaderMobile : {}) }}>
          <div style={{ minWidth: 0 }}>
            <div style={styles.drawerMetaLine}>
              <StatusBadge status={status} />
              <span>{issue.ticker || "-"}</span>
              <span>{formatDate(issue.rceptDt)}</span>
            </div>
            <h2 style={{ ...styles.drawerTitle, ...(isMobile ? styles.drawerTitleMobile : {}) }}>
              {cleanText(issue.issueTitle || issue.reportNm) || "이벤트 상세"}
            </h2>
            <p style={{ ...styles.drawerSub, ...(isMobile ? styles.drawerSubMobile : {}) }}>
              {issue.corpName || "종목명 없음"} · {issueTypeLabelMap[issue.issueType]}
            </p>
          </div>
          <button
            type="button"
            style={{ ...styles.closeButton, ...(isMobile ? styles.closeButtonMobile : {}) }}
            onClick={onClose}
            aria-label="상세 닫기"
          >
            ×
          </button>
        </div>

        <div style={isMobile ? styles.drawerBodyMobile : undefined}>
          <div style={{ ...styles.drawerSection, ...(isMobile ? styles.drawerSectionMobile : {}) }}>
            <div style={styles.drawerSectionTitle}>Event Summary</div>
            <p style={{ ...styles.drawerText, ...(isMobile ? styles.drawerTextMobile : {}) }}>
              {getIssueBody(issue) || buildIssueHeadline(issue)}
            </p>
          </div>

          <div style={{ ...styles.drawerSection, ...(isMobile ? styles.drawerSectionMobile : {}) }}>
            <div style={styles.drawerSectionTitle}>Reasoning Path</div>
            <p style={{ ...styles.drawerText, ...(isMobile ? styles.drawerTextMobile : {}) }}>{inferWhyImportant(issue)}</p>
          </div>

          <div style={{ ...styles.drawerSection, ...(isMobile ? styles.drawerSectionMobile : {}) }}>
            <div style={styles.drawerSectionTitle}>Related Asset</div>
            <div style={styles.relatedAssetList}>
              {relatedAssets.map((asset) => (
                <div key={asset.ticker} style={{ ...styles.relatedAssetItem, ...(isMobile ? styles.relatedAssetItemMobile : {}) }}>
                  <span style={styles.assetTickerBadge}>{asset.ticker}</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={styles.relatedAssetName}>{asset.name}</div>
                    <div style={styles.relatedAssetDesc}>{asset.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ ...styles.drawerSection, ...(isMobile ? styles.drawerSectionMobile : {}) }}>
            <div style={styles.impactHeader}>
              <span>Impact</span>
              <strong>{getDirection(issue)} · {score}점</strong>
            </div>
            <div style={styles.progressTrackLarge}>
              <div
                style={{
                  ...styles.progressFill,
                  width: `${score}%`,
                  background: status === "risk" ? UI.red : status === "action" ? UI.green : status === "review" ? UI.amber : UI.slate,
                }}
              />
            </div>
          </div>

          <div style={{ ...styles.drawerSection, ...(isMobile ? styles.drawerSectionMobile : {}) }}>
            <div style={styles.drawerSectionTitle}>Evidence</div>
            <div style={styles.evidenceBox}>
              <div style={styles.evidenceRow}>
                <strong style={styles.evidenceLabel}>공시일</strong>
                <span style={styles.evidenceValue}>{formatDate(issue.rceptDt)}</span>
              </div>
              <div style={styles.evidenceRow}>
                <strong style={styles.evidenceLabel}>공시번호</strong>
                <span style={styles.evidenceValue}>{issue.rceptNo || "-"}</span>
              </div>
              <div style={styles.evidenceRow}>
                <strong style={styles.evidenceLabel}>공시명</strong>
                <span style={styles.evidenceValue}>{issue.reportNm || "-"}</span>
              </div>
              <div style={styles.evidenceRow}>
                <strong style={styles.evidenceLabel}>판단상태</strong>
                <span style={styles.evidenceValue}>{getHealthLabel(status)} · {score}점</span>
              </div>
            </div>
          </div>
        </div>

        <div style={{ ...styles.drawerActionBar, ...(isMobile ? styles.drawerActionBarMobile : {}) }}>
          {issue.sourceUrl ? (
            <a
              href={issue.sourceUrl}
              target="_blank"
              rel="noreferrer"
              onClick={() =>
                trackUxEvent("issue_external_link_click", {
                  source: "issue-detail-drawer",
                  destination: "dart",
                  issue_id: issue.id,
                  ticker: issue.ticker || undefined,
                  rcept_no: issue.rceptNo || undefined,
                })
              }
              style={{ ...styles.primaryButton, ...styles.drawerPrimaryAction }}
            >
              DART 원문 보기
            </a>
          ) : null}
          <button type="button" style={{ ...styles.secondaryButton, ...styles.drawerSecondaryAction }} onClick={onClose}>
            닫기
          </button>
        </div>
      </aside>
    </>
  );
}

export default function IssueUpdatesPage({
  apiBaseUrl = "/api/issues",
  pageSize = 20,
}: IssueUpdatesPageProps) {
  const isMobile = useIsMobile();

  const [searchInput, setSearchInput] = useState<string>("");
  const [keyword, setKeyword] = useState<string>("");
  const [tab, setTab] = useState<string>("all");
  const [page, setPage] = useState<number>(0);

  const [data, setData] = useState<IssuePageResponse>(initialPageResponse);
  const [allItems, setAllItems] = useState<StockIssueUpdate[]>([]);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [refreshSeed, setRefreshSeed] = useState<number>(0);

  const [sortBy, setSortBy] = useState<SortType>("priority");
  const [datePreset, setDatePreset] = useState<string>("recent");
  const [detailImpactLevel, setDetailImpactLevel] = useState<string>("all");
  const [detailIssueType, setDetailIssueType] = useState<string>("all");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [selectedIssue, setSelectedIssue] = useState<StockIssueUpdate | null>(null);

  const effectiveIssueType = useMemo(() => {
    if (tab === "approval") return "approval";
    if (tab === "contract") return "contract";
    if (tab === "funding") return "funding";
    return "all";
  }, [tab]);

  const effectiveImpactLevel = useMemo(() => {
    if (tab === "hot") return "high";
    return "all";
  }, [tab]);

  const filteredItems = useMemo(() => {
    return applyClientFilters({
      items: allItems,
      datePreset,
      detailImpactLevel,
      detailIssueType,
    });
  }, [allItems, datePreset, detailImpactLevel, detailIssueType]);

  const sortedFilteredItems = useMemo(() => {
    return sortItems(filteredItems, sortBy);
  }, [filteredItems, sortBy]);

  const clientTotalPages = useMemo(() => {
    return Math.max(Math.ceil(sortedFilteredItems.length / pageSize), 1);
  }, [sortedFilteredItems.length, pageSize]);

  const pagedItems = useMemo(() => {
    const startIndex = page * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedFilteredItems.slice(startIndex, endIndex);
  }, [sortedFilteredItems, page, pageSize]);

  const focusEvents = useMemo(() => getTopEvents(filteredItems, isMobile ? 1 : 3), [filteredItems, isMobile]);
  const statusCounts = useMemo(() => getStatusCounts(filteredItems), [filteredItems]);

  const stats = useMemo(() => {
    const serverTotal = data.totalElements || 0;
    const currentCount = filteredItems.length;
    const highCount = filteredItems.filter((item) => item.impactLevel === "high").length;
    const riskCount = filteredItems.filter(isRiskIssue).length;
    const latestDate = sortedFilteredItems[0]?.rceptDt ? formatDate(sortedFilteredItems[0].rceptDt) : "-";
    const avgPriority =
      filteredItems.length > 0
        ? Math.round(filteredItems.reduce((sum, item) => sum + calculatePriorityScore(item), 0) / filteredItems.length)
        : 0;

    return { serverTotal, currentCount, highCount, riskCount, latestDate, avgPriority };
  }, [data.totalElements, filteredItems, sortedFilteredItems]);

  const heroSummary = useMemo(() => getHeroSummary(filteredItems), [filteredItems]);

  const shouldShowPagination = useMemo(() => {
    if (loading || error) return false;
    return clientTotalPages > 1 && filteredItems.length > 0;
  }, [loading, error, clientTotalPages, filteredItems.length]);

  const changePage = useCallback((nextPage: number) => {
    setPage((prevPage) => {
      const maxPage = Math.max(clientTotalPages - 1, 0);
      const safePage = Math.min(Math.max(nextPage, 0), maxPage);
      if (safePage === prevPage) return prevPage;

      trackUxEvent("issue_page_change", {
        source: "issue-pagination",
        from_page: prevPage + 1,
        to_page: safePage + 1,
      });
      return safePage;
    });
  }, [clientTotalPages]);

  useEffect(() => {
    setPage(0);
  }, [keyword, tab, datePreset, detailImpactLevel, detailIssueType, sortBy]);

  useEffect(() => {
    if (page > clientTotalPages - 1) setPage(Math.max(clientTotalPages - 1, 0));
  }, [page, clientTotalPages]);

  useEffect(() => {
    const controller = new AbortController();
    const startedAt = performance.now();

    async function fetchAllIssues() {
      setLoading(true);
      setError("");

      try {
        // Default entry is bounded to today + yesterday. The server already
        // sorts rcept_dt DESC, so we fetch in small batches and stop as soon
        // as the oldest loaded row crosses the requested date window.
        // This removes the previous behavior that downloaded every historical page.
        const fetchSize = Math.max(pageSize, 100);
        const lowerBound = getFetchLowerBound(datePreset);

        const buildPageUrl = (serverPage: number) =>
          buildApiUrl({
            apiBaseUrl,
            page: serverPage,
            size: fetchSize,
            keyword,
            impactLevel: effectiveImpactLevel,
            issueType: effectiveIssueType,
          });

        const firstResult = await requestIssuePage(buildPageUrl(0), controller.signal);
        let mergedItems = [...(firstResult.content || [])];
        let nextServerPage = 1;

        const hasCrossedLowerBound = (items: StockIssueUpdate[]) => {
          if (!lowerBound || items.length === 0) return false;
          const oldestLoadedDate = items[items.length - 1]?.rceptDt;
          return Boolean(oldestLoadedDate && oldestLoadedDate < lowerBound);
        };

        while (nextServerPage < firstResult.totalPages && !hasCrossedLowerBound(mergedItems)) {
          const batchEnd = Math.min(
            nextServerPage + ISSUE_FETCH_CONCURRENCY,
            firstResult.totalPages,
          );
          const batchUrls: string[] = [];

          for (let serverPage = nextServerPage; serverPage < batchEnd; serverPage += 1) {
            batchUrls.push(buildPageUrl(serverPage));
          }

          const batchResults = await fetchIssuePagesWithConcurrency(
            batchUrls,
            controller.signal,
          );
          for (const result of batchResults) {
            mergedItems = mergedItems.concat(result.content || []);
          }
          nextServerPage = batchEnd;
        }

        if (!controller.signal.aborted) {
          setAllItems(mergedItems);
          setData({
            ...firstResult,
            content: mergedItems,
            totalPages: Math.max(Math.ceil(mergedItems.length / pageSize), 1),
            number: 0,
            first: true,
            last: mergedItems.length <= pageSize,
            empty: mergedItems.length === 0,
          });

          trackUxEvent("issue_list_loaded", {
            source: "issue-page",
            result_count: mergedItems.length,
            server_total: firstResult.totalElements || 0,
            server_pages: firstResult.totalPages || 0,
            fetched_server_pages: Math.max(nextServerPage, 1),
            date_preset: datePreset,
            lower_bound: lowerBound || undefined,
            keyword: keyword || undefined,
            impact_level: effectiveImpactLevel,
            issue_type: effectiveIssueType,
            elapsedMs: Math.round(performance.now() - startedAt),
          });
        }
      } catch (error: any) {
        if (controller.signal.aborted) return;

        setError("이벤트 공시 데이터를 불러오지 못했습니다.");
        setAllItems([]);
        setData(initialPageResponse);

        trackUxEvent("issue_list_load_error", {
          source: "issue-page",
          date_preset: datePreset,
          keyword: keyword || undefined,
          impact_level: effectiveImpactLevel,
          issue_type: effectiveIssueType,
          error: String(error?.message || "unknown").slice(0, 160),
          elapsedMs: Math.round(performance.now() - startedAt),
        });
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    void fetchAllIssues();

    return () => {
      controller.abort();
    };
  }, [apiBaseUrl, pageSize, keyword, effectiveImpactLevel, effectiveIssueType, datePreset, refreshSeed]);

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    if (value.trim() === "") setKeyword("");
  };

  const handleSearchSubmit = () => {
    const nextKeyword = searchInput.trim();
    setPage(0);
    setKeyword(nextKeyword);
    trackUxEvent("issue_search", {
      source: "issue-search",
      keyword: nextKeyword || undefined,
    });
  };

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSearchSubmit();
  };

  const resetFilters = () => {
    setSearchInput("");
    setKeyword("");
    setTab("all");
    setSortBy("priority");
    setDatePreset("recent");
    setDetailImpactLevel("all");
    setDetailIssueType("all");
    setPage(0);
    setExpandedId(null);
    trackUxEvent("issue_filter_apply", {
      source: "issue-filter",
      filter_type: "reset",
      filter_value: "all",
    });
  };

  const handleTabChange = (value: string) => {
    setTab(value);
    setPage(0);
    trackUxEvent("issue_filter_apply", {
      source: "issue-tab",
      filter_type: "category",
      filter_value: value,
    });
  };

  const handleSortChange = (value: SortType) => {
    setSortBy(value);
    trackUxEvent("issue_filter_apply", {
      source: "issue-filter",
      filter_type: "sort",
      filter_value: value,
    });
  };

  const handleDatePresetChange = (value: string) => {
    setDatePreset(value);
    trackUxEvent("issue_filter_apply", {
      source: "issue-filter",
      filter_type: "date",
      filter_value: value,
    });
  };

  const handleImpactChange = (value: string) => {
    setDetailImpactLevel(value);
    trackUxEvent("issue_filter_apply", {
      source: "issue-filter",
      filter_type: "impact_level",
      filter_value: value,
    });
  };

  const handleIssueTypeChange = (value: string) => {
    setDetailIssueType(value);
    trackUxEvent("issue_filter_apply", {
      source: "issue-filter",
      filter_type: "issue_type",
      filter_value: value,
    });
  };

  const handleToggleExpanded = (id: number) => {
    const issue = allItems.find((item) => item.id === id);
    setExpandedId((prev) => {
      const opening = prev !== id;
      if (opening) {
        trackUxEvent("issue_item_open", {
          source: "issue-list-expand",
          issue_id: id,
          ticker: issue?.ticker || undefined,
          rcept_no: issue?.rceptNo || undefined,
          issue_type: issue?.issueType || undefined,
          impact_level: issue?.impactLevel || undefined,
        });
      }
      return opening ? id : null;
    });
  };

  const handleSelectIssue = (issue: StockIssueUpdate, source = "issue-detail") => {
    setSelectedIssue(issue);
    trackUxEvent("issue_item_open", {
      source,
      issue_id: issue.id,
      ticker: issue.ticker || undefined,
      rcept_no: issue.rceptNo || undefined,
      issue_type: issue.issueType,
      impact_level: issue.impactLevel,
      priority_score: calculatePriorityScore(issue),
    });
  };

  const handleCloseIssue = () => {
    setSelectedIssue(null);
  };

  const handleRefresh = () => {
    trackUxEvent("issue_refresh", { source: "issue-page" });
    setRefreshSeed((prev) => prev + 1);
  };

  return (
    <div style={styles.page}>
      <div style={{ ...styles.shell, ...(isMobile ? styles.shellMobile : styles.shellDesktop) }}>
        <section style={{ ...styles.hero, ...(isMobile ? styles.heroMobile : {}) }}>
          <div>
            <div style={{ ...styles.heroKicker, ...(isMobile ? styles.heroKickerMobile : {}) }}>PolicyIntel Intelligence</div>
            <h1 style={{ ...styles.heroTitle, ...(isMobile ? styles.heroTitleMobile : {}) }}>실시간 공시</h1>
            <p style={{ ...styles.heroText, ...(isMobile ? styles.heroTextMobile : {}) }}>
              공시를 단순 나열하지 않고, 영향도·리스크·우선순위 기준으로 먼저 확인해야 할 이벤트를 정리합니다.
            </p>
          </div>

          <button type="button" onClick={handleRefresh} style={{ ...styles.refreshButton, ...(isMobile ? styles.refreshButtonMobile : {}) }}>
            {isMobile ? "새로고침 ↻" : "새로고침"}
          </button>
        </section>

        <SearchFilterBar
          isMobile={isMobile}
          searchInput={searchInput}
          tab={tab}
          sortBy={sortBy}
          datePreset={datePreset}
          detailImpactLevel={detailImpactLevel}
          detailIssueType={detailIssueType}
          onSearchInputChange={handleSearchInputChange}
          onSearchSubmit={handleSearchSubmit}
          onSearchKeyDown={handleSearchKeyDown}
          onTabChange={handleTabChange}
          onSortChange={handleSortChange}
          onDatePresetChange={handleDatePresetChange}
          onImpactChange={handleImpactChange}
          onIssueTypeChange={handleIssueTypeChange}
          onReset={resetFilters}
        />

        <MarketPerspectiveCard
          isMobile={isMobile}
          summary={heroSummary}
          stats={stats}
          statusCounts={statusCounts}
        />

        {!isMobile && (
          <section style={{ ...styles.metricGrid, gridTemplateColumns: "repeat(5, minmax(0, 1fr))" }}>
            <MetricCard label="Total Events" value={stats.serverTotal} sub="서버 전체" />
            <MetricCard label="Filtered" value={stats.currentCount} sub="현재 조건" />
            <MetricCard label="High Impact" value={stats.highCount} sub="고영향 공시" tone="good" />
            <MetricCard label="Risk" value={stats.riskCount} sub="부담성 후보" tone={stats.riskCount > 0 ? "warn" : "default"} />
            <MetricCard label="Avg Priority" value={stats.avgPriority} sub={`최신 ${stats.latestDate}`} tone="review" />
          </section>
        )}

        {loading && <div style={styles.noticeCard}>이벤트 공시 데이터를 불러오는 중입니다.</div>}
        {error && !loading && <div style={styles.errorCard}>{error}</div>}

        {!loading && !error && (
          <>
            <FocusEventsSection events={focusEvents} isMobile={isMobile} onSelect={(issue) => handleSelectIssue(issue, "issue-focus-card")} />

            {pagedItems.length === 0 ? (
              <div style={styles.emptyCard}>
                <div style={styles.emptyTitle}>표시할 이벤트 공시가 없습니다.</div>
                <div style={styles.emptyDesc}>검색어를 바꾸거나 필터를 해제한 뒤 다시 확인하세요.</div>
              </div>
            ) : (
              <AssetHealthList
                items={pagedItems}
                isMobile={isMobile}
                expandedId={expandedId}
                onToggle={handleToggleExpanded}
                onSelect={(issue) => handleSelectIssue(issue, "issue-list-detail")}
              />
            )}

            {shouldShowPagination && (
              <Pagination page={page} totalPages={clientTotalPages} onChange={changePage} isMobile={isMobile} />
            )}
          </>
        )}

        <EventDetailDrawer issue={selectedIssue} onClose={handleCloseIssue} isMobile={isMobile} />
      </div>
    </div>
  );
}

const UI = {
  bg: "var(--pi-canvas, #f4f7fa)",
  card: "var(--pi-surface, #ffffff)",
  cardSoft: "var(--pi-surface-subtle, #edf2f6)",
  border: "var(--pi-border-quiet, #e2e8ef)",
  borderStrong: "var(--pi-border-strong, #cbd5df)",
  text: "var(--pi-text, #07111f)",
  textSub: "var(--pi-text-secondary, #596779)",
  textMuted: "var(--pi-text-muted, #8491a1)",
  blue: "var(--pi-blue, #2968ff)",
  blueSoft: "color-mix(in srgb, var(--pi-blue, #2968ff) 8%, white)",
  green: "var(--pi-positive, #168a5b)",
  greenSoft: "color-mix(in srgb, var(--pi-positive, #168a5b) 9%, white)",
  amber: "var(--pi-watch, #c78118)",
  amberSoft: "color-mix(in srgb, var(--pi-watch, #c78118) 9%, white)",
  red: "var(--pi-risk, #d94a4a)",
  redSoft: "color-mix(in srgb, var(--pi-risk, #d94a4a) 8%, white)",
  slate: "var(--pi-neutral, #6f7f91)",
  slateSoft: "color-mix(in srgb, var(--pi-neutral, #6f7f91) 8%, white)",
};

const statusStyle: Record<HealthStatus, React.CSSProperties> = {
  normal: {
    color: UI.green,
    background: UI.greenSoft,
    borderColor: `color-mix(in srgb, ${UI.green} 28%, transparent)`,
  },
  review: {
    color: UI.amber,
    background: UI.amberSoft,
    borderColor: `color-mix(in srgb, ${UI.amber} 28%, transparent)`,
  },
  action: {
    color: UI.blue,
    background: UI.blueSoft,
    borderColor: `color-mix(in srgb, ${UI.blue} 24%, transparent)`,
  },
  risk: {
    color: UI.red,
    background: UI.redSoft,
    borderColor: `color-mix(in srgb, ${UI.red} 24%, transparent)`,
  },
};

const statusBorderColor: Record<HealthStatus, string> = {
  normal: UI.green,
  review: UI.amber,
  action: UI.blue,
  risk: UI.red,
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background: UI.bg,
    color: UI.text,
  },
  shell: {
    maxWidth: 1500,
    margin: "0 auto",
  },
  hero: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 18,
    marginBottom: 16,
  },
  heroKicker: {
    color: UI.blue,
    fontSize: 13,
    fontWeight: 900,
    letterSpacing: 0.2,
    marginBottom: 6,
  },
  heroTitle: {
    margin: 0,
    fontSize: 30,
    lineHeight: 1.15,
    fontWeight: 950,
    letterSpacing: "-0.04em",
  },
  heroText: {
    margin: "10px 0 0",
    color: UI.textSub,
    fontSize: 15,
    lineHeight: 1.7,
    maxWidth: 900,
  },
  refreshButton: {
    height: 40,
    borderRadius: 12,
    border: `1px solid ${UI.borderStrong}`,
    background: UI.card,
    color: UI.text,
    padding: "0 16px",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 1px 3px rgba(15,23,42,.08)",
    whiteSpace: "nowrap",
  },
  filterPanel: {
    background: UI.cardSoft,
    border: `1px solid ${UI.border}`,
    borderRadius: 8,
    padding: 14,
    boxShadow: "none",
    marginBottom: 14,
  },
  searchLine: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 12,
  },
  searchInput: {
    flex: "1 1 320px",
    height: 42,
    borderRadius: 12,
    border: `1px solid ${UI.border}`,
    background: UI.cardSoft,
    color: UI.text,
    padding: "0 14px",
    outline: "none",
    fontSize: 14,
  },
  primaryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: 40,
    borderRadius: 12,
    border: `1px solid ${UI.blue}`,
    background: UI.blue,
    color: "#fff",
    padding: "0 14px",
    fontSize: 13,
    fontWeight: 850,
    textDecoration: "none",
    cursor: "pointer",
    whiteSpace: "nowrap",
    boxSizing: "border-box",
  },
  secondaryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: 40,
    borderRadius: 12,
    border: `1px solid ${UI.borderStrong}`,
    background: UI.card,
    color: UI.text,
    padding: "0 14px",
    fontSize: 13,
    fontWeight: 800,
    textDecoration: "none",
    cursor: "pointer",
    whiteSpace: "nowrap",
    boxSizing: "border-box",
  },
  tabRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: 12,
  },
  tabButton: {
    border: `1px solid ${UI.border}`,
    background: UI.cardSoft,
    color: UI.textSub,
    borderRadius: 999,
    padding: "8px 13px",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
  },
  tabButtonActive: {
    background: UI.blueSoft,
    color: UI.blue,
    borderColor: `color-mix(in srgb, ${UI.blue} 24%, transparent)`,
  },
  filterGrid: {
    display: "grid",
    gap: 10,
  },
  selectField: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  selectLabel: {
    fontSize: 12,
    color: UI.textMuted,
    fontWeight: 800,
  },
  select: {
    width: "100%",
    height: 40,
    borderRadius: 12,
    border: `1px solid ${UI.border}`,
    background: UI.cardSoft,
    color: UI.text,
    padding: "0 12px",
    fontSize: 13,
    outline: "none",
    boxSizing: "border-box",
  },
  marketCard: {
    background: "transparent",
    borderTop: `1px solid ${UI.border}`,
    borderBottom: `1px solid ${UI.border}`,
    borderLeft: 0,
    borderRight: 0,
    borderRadius: 0,
    padding: "16px 0",
    boxShadow: "none",
    marginBottom: 14,
  },
  marketHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 14,
    flexWrap: "wrap",
    marginBottom: 22,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 950,
  },
  cardSub: {
    marginTop: 4,
    fontSize: 13,
    color: UI.textMuted,
  },
  statusSummary: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  miniPill: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    border: "1px solid",
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 900,
  },
  marketSummary: {
    margin: "0 0 22px",
    color: UI.textSub,
    fontSize: 15,
    lineHeight: 1.75,
  },
  marketGrid: {
    display: "grid",
    gap: 30,
  },
  sectionEyebrow: {
    color: UI.textMuted,
    fontSize: 12,
    fontWeight: 950,
    letterSpacing: 1.4,
    textTransform: "uppercase",
    marginBottom: 12,
  },
  simpleRadarBox: {
    borderRadius: 0,
    background: "transparent",
    border: 0,
    borderTop: `1px solid ${UI.border}`,
    padding: "12px 0 0",
  },
  radarLine: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    color: UI.textSub,
    fontSize: 13,
    fontWeight: 700,
    margin: "8px 0",
  },
  progressTrack: {
    width: "100%",
    height: 7,
    borderRadius: 999,
    background: "#e7edf4",
    overflow: "hidden",
    marginBottom: 10,
  },
  progressTrackLarge: {
    width: "100%",
    height: 8,
    borderRadius: 999,
    background: "#e7edf4",
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    borderRadius: 999,
  },
  directionBox: {
    borderRadius: 0,
    background: "transparent",
    border: 0,
    borderTop: `1px solid ${UI.border}`,
    padding: "12px 0 0",
    minHeight: 157,
  },
  directionTitle: {
    fontSize: 20,
    fontWeight: 950,
    color: UI.text,
    marginBottom: 8,
  },
  directionSub: {
    color: UI.textSub,
    fontSize: 14,
    lineHeight: 1.6,
  },
  directionStats: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    marginTop: 18,
  },
  directionStat: {
    background: "transparent",
    border: 0,
    borderLeft: `1px solid ${UI.border}`,
    borderRadius: 0,
    padding: "8px 12px",
  },
  metricGrid: {
    display: "grid",
    gap: 0,
    marginBottom: 18,
    background: UI.card,
    borderTop: `1px solid ${UI.border}`,
    borderBottom: `1px solid ${UI.border}`,
  },
  metricCard: {
    background: "transparent",
    border: 0,
    borderRight: `1px solid ${UI.border}`,
    borderRadius: 0,
    padding: "11px 14px",
    boxShadow: "none",
    minWidth: 0,
  },
  metricLabel: {
    fontSize: 12,
    color: UI.textMuted,
    fontWeight: 850,
    marginBottom: 8,
  },
  metricValue: {
    fontSize: 24,
    fontWeight: 950,
    letterSpacing: "-0.03em",
  },
  metricSub: {
    marginTop: 4,
    fontSize: 12,
    color: UI.textSub,
  },
  focusSection: {
    marginBottom: 18,
    borderTop: `1px solid ${UI.border}`,
    paddingTop: 16,
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginBottom: 12,
    gap: 12,
  },
  sectionTitle: {
    margin: 0,
    fontSize: 22,
    fontWeight: 950,
    letterSpacing: "-0.03em",
  },
  sectionDesc: {
    margin: "6px 0 0",
    color: UI.textSub,
    fontSize: 14,
  },
  focusGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 0,
    borderTop: `1px solid ${UI.border}`,
  },
  focusCard: {
    background: "transparent",
    border: 0,
    borderBottom: `1px solid ${UI.border}`,
    borderRadius: 0,
    padding: "14px 4px",
    boxShadow: "none",
    cursor: "pointer",
  },
  focusTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    marginBottom: 8,
  },
  focusTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 950,
    lineHeight: 1.35,
  },
  focusMeta: {
    marginTop: 7,
    color: UI.textMuted,
    fontSize: 12,
    fontWeight: 700,
  },
  focusSummary: {
    color: UI.textSub,
    fontSize: 13,
    lineHeight: 1.6,
    minHeight: 0,
    margin: "0 0 10px",
  },
  impactRow: {
    display: "flex",
    justifyContent: "space-between",
    color: UI.textSub,
    fontSize: 13,
    fontWeight: 800,
    marginBottom: 8,
  },
  relatedBox: {
    marginTop: 14,
  },
  relatedTitle: {
    color: UI.textMuted,
    fontSize: 12,
    fontWeight: 950,
    letterSpacing: 1.2,
    textTransform: "uppercase",
    marginBottom: 8,
  },
  relatedLine: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    color: UI.textSub,
    minWidth: 0,
  },
  tickerChip: {
    display: "inline-flex",
    border: `1px solid ${UI.borderStrong}`,
    borderRadius: 8,
    padding: "3px 7px",
    color: UI.text,
    background: UI.cardSoft,
    fontWeight: 900,
    fontSize: 12,
  },
  cardLinkButton: {
    marginTop: 8,
    width: "auto",
    height: 32,
    border: "none",
    background: "transparent",
    color: UI.blue,
    fontSize: 12,
    fontWeight: 850,
    cursor: "pointer",
    padding: 0,
  },
  assetPanel: {
    background: "transparent",
    borderTop: `1px solid ${UI.border}`,
    borderBottom: `1px solid ${UI.border}`,
    borderLeft: 0,
    borderRight: 0,
    borderRadius: 0,
    padding: "16px 0 0",
    boxShadow: "none",
  },
  assetHeader: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 10,
    padding: "0 4px",
  },
  assetTitle: {
    margin: 0,
    fontSize: 22,
    fontWeight: 950,
    letterSpacing: "-0.03em",
  },
  assetList: {
    display: "flex",
    flexDirection: "column",
    gap: 0,
    borderTop: `1px solid ${UI.border}`,
  },
  assetRow: {
    border: 0,
    borderLeft: "3px solid",
    borderBottom: `1px solid ${UI.border}`,
    borderRadius: 0,
    background: UI.card,
    overflow: "hidden",
  },
  assetRowMain: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "minmax(170px, .72fr) minmax(280px, 1.8fr) 88px 30px",
    alignItems: "center",
    gap: 14,
    border: "none",
    background: "transparent",
    padding: "12px 14px",
    textAlign: "left",
    cursor: "pointer",
  },
  assetLeft: {
    minWidth: 0,
  },
  assetNameLine: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  tickerLarge: {
    display: "inline-flex",
    alignItems: "center",
    border: `1px solid ${UI.borderStrong}`,
    borderRadius: 8,
    padding: "3px 8px",
    fontWeight: 950,
    color: UI.text,
    background: UI.cardSoft,
  },
  assetCorp: {
    marginTop: 6,
    color: UI.textSub,
    fontSize: 13,
    fontWeight: 700,
  },
  assetMiddle: {
    minWidth: 0,
  },
  assetEvent: {
    color: UI.text,
    fontWeight: 900,
    fontSize: 14,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  assetSub: {
    marginTop: 6,
    color: UI.textMuted,
    fontSize: 12,
    fontWeight: 700,
  },
  assetRight: {
    textAlign: "right",
  },
  scoreValue: {
    fontSize: 18,
    fontWeight: 950,
    color: UI.text,
  },
  scoreSub: {
    fontSize: 11,
    color: UI.textMuted,
    marginTop: 3,
  },
  chevron: {
    color: UI.textSub,
    fontSize: 20,
    textAlign: "center",
  },
  assetExpanded: {
    borderTop: `1px solid ${UI.border}`,
    padding: "14px 16px 16px",
    background: UI.cardSoft,
  },
  expandedGrid: {
    display: "grid",
    gap: 12,
  },
  expandedLabel: {
    color: UI.textMuted,
    fontSize: 12,
    fontWeight: 950,
    textTransform: "uppercase",
    letterSpacing: 0.6,
    marginBottom: 8,
  },
  expandedText: {
    margin: 0,
    color: UI.textSub,
    fontSize: 13,
    lineHeight: 1.65,
  },
  expandedActionRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    flexWrap: "wrap",
    marginTop: 14,
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    border: "1px solid",
    padding: "5px 10px",
    fontSize: 12,
    fontWeight: 950,
    whiteSpace: "nowrap",
  },
  noticeCard: {
    background: UI.card,
    border: `1px solid ${UI.border}`,
    borderRadius: 16,
    padding: 18,
    color: UI.textSub,
    marginBottom: 16,
  },
  errorCard: {
    background: UI.redSoft,
    border: "1px solid #ffc0bb",
    borderRadius: 16,
    padding: 18,
    color: UI.red,
    marginBottom: 16,
    fontWeight: 850,
  },
  emptyCard: {
    background: UI.card,
    border: `1px solid ${UI.border}`,
    borderRadius: 18,
    padding: 28,
    textAlign: "center",
  },
  emptyTitle: {
    fontSize: 17,
    fontWeight: 950,
    color: UI.text,
  },
  emptyDesc: {
    marginTop: 8,
    color: UI.textSub,
    fontSize: 14,
  },
  paginationWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    marginTop: 20,
  },
  paginationNumbers: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
    justifyContent: "center",
  },
  pageButton: {
    minWidth: 38,
    height: 38,
    borderRadius: 11,
    border: `1px solid ${UI.border}`,
    background: UI.card,
    color: UI.textSub,
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
  },
  pageButtonActive: {
    background: UI.blue,
    color: "#fff",
    borderColor: UI.blue,
  },
  drawerBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,.52)",
    zIndex: 50,
  },
  drawer: {
    position: "fixed",
    top: 0,
    right: 0,
    width: "min(520px, 92vw)",
    height: "100vh",
    background: UI.card,
    zIndex: 51,
    boxShadow: "-12px 0 34px rgba(15,23,42,.22)",
    borderLeft: `1px solid ${UI.border}`,
    overflowY: "auto",
  },
  drawerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 14,
    padding: "22px 22px 18px",
    borderBottom: `1px solid ${UI.border}`,
  },
  drawerTitle: {
    margin: 0,
    fontSize: 20,
    fontWeight: 950,
    letterSpacing: "-0.03em",
    lineHeight: 1.35,
  },
  drawerSub: {
    margin: "8px 0 0",
    color: UI.textSub,
    fontSize: 13,
  },
  closeButton: {
    width: 34,
    height: 34,
    borderRadius: 10,
    border: `1px solid ${UI.borderStrong}`,
    background: UI.card,
    color: UI.textSub,
    fontSize: 22,
    lineHeight: 1,
    cursor: "pointer",
  },
  drawerSection: {
    padding: "20px 22px",
    borderBottom: `1px solid ${UI.border}`,
  },
  drawerSectionTitle: {
    fontSize: 14,
    fontWeight: 950,
    marginBottom: 12,
  },
  drawerText: {
    color: UI.textSub,
    fontSize: 14,
    lineHeight: 1.75,
    margin: 0,
  },
  relatedAssetList: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  relatedAssetItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    border: `1px solid ${UI.border}`,
    background: UI.cardSoft,
    borderRadius: 12,
    padding: 12,
  },
  assetTickerBadge: {
    display: "inline-flex",
    minWidth: 52,
    justifyContent: "center",
    borderRadius: 9,
    background: UI.blueSoft,
    color: UI.blue,
    fontWeight: 950,
    padding: "5px 8px",
    fontSize: 12,
  },
  relatedAssetName: {
    color: UI.text,
    fontWeight: 900,
    fontSize: 14,
  },
  relatedAssetDesc: {
    color: UI.textSub,
    fontSize: 12,
    marginTop: 4,
  },
  impactHeader: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 13,
    color: UI.textSub,
    fontWeight: 850,
    marginBottom: 8,
  },
  evidenceBox: {
    display: "grid",
    gap: 8,
  },
  shellDesktop: {
    padding: "28px 24px 48px",
  },
  shellMobile: {
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    padding: "14px 10px calc(118px + env(safe-area-inset-bottom))",
    overflowX: "hidden",
  },
  heroMobile: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    alignItems: "start",
    gap: 10,
    marginBottom: 12,
  },
  heroKickerMobile: {
    fontSize: 11,
    marginBottom: 4,
  },
  heroTitleMobile: {
    fontSize: 23,
    lineHeight: 1.15,
    letterSpacing: "-0.045em",
  },
  heroTextMobile: {
    gridColumn: "1 / -1",
    marginTop: 7,
    fontSize: 12.5,
    lineHeight: 1.55,
    maxWidth: "none",
  },
  refreshButtonMobile: {
    height: 34,
    borderRadius: 10,
    padding: "0 10px",
    fontSize: 11.5,
    flex: "0 0 auto",
  },
  filterPanelMobile: {
    padding: 12,
    borderRadius: 15,
    marginBottom: 12,
  },
  mobileSearchBox: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 64px",
    gap: 8,
  },
  searchInputMobile: {
    minWidth: 0,
    width: "100%",
    height: 40,
    fontSize: 13,
    boxSizing: "border-box",
  },
  mobileSearchButton: {
    width: 64,
    minWidth: 64,
    height: 40,
    padding: 0,
  },
  mobileFilterActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    marginTop: 8,
  },
  mobileFilterToggle: {
    height: 34,
    borderRadius: 10,
    padding: "0 11px",
    fontSize: 11.5,
  },
  mobileResetButton: {
    border: "none",
    background: "transparent",
    color: UI.textMuted,
    padding: "6px 4px",
    fontSize: 11.5,
    fontWeight: 800,
    cursor: "pointer",
  },
  mobileTabScroller: {
    display: "flex",
    gap: 7,
    marginTop: 10,
    paddingBottom: 2,
    overflowX: "auto",
    overscrollBehaviorX: "contain",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
  },
  mobileTabButton: {
    flex: "0 0 auto",
    padding: "7px 11px",
    fontSize: 11.5,
    whiteSpace: "nowrap",
  },
  filterGridMobile: {
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 8,
    marginTop: 10,
    paddingTop: 10,
    borderTop: `1px solid ${UI.border}`,
  },
  mobileSnapshotCard: {
    background: UI.card,
    border: `1px solid ${UI.border}`,
    borderRadius: 16,
    padding: 13,
    marginBottom: 14,
    boxShadow: "0 2px 8px rgba(15,23,42,.045)",
  },
  mobileSnapshotHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 10,
  },
  mobileSnapshotEyebrow: {
    color: UI.textMuted,
    fontSize: 10.5,
    fontWeight: 900,
    marginBottom: 3,
  },
  mobileSnapshotTitle: {
    color: UI.text,
    fontSize: 18,
    fontWeight: 950,
    letterSpacing: "-0.035em",
  },
  mobileSnapshotDate: {
    color: UI.textMuted,
    fontSize: 10.5,
    fontWeight: 800,
    whiteSpace: "nowrap",
  },
  mobileStatusScroller: {
    display: "flex",
    gap: 6,
    overflowX: "auto",
    padding: "10px 0 2px",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
  },
  mobileSnapshotSummary: {
    margin: "10px 0 0",
    paddingTop: 10,
    borderTop: `1px solid ${UI.border}`,
    color: UI.textSub,
    fontSize: 12.5,
    lineHeight: 1.6,
    wordBreak: "keep-all",
    overflowWrap: "anywhere",
  },
  mobileSnapshotGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 6,
    marginTop: 12,
  },
  mobileSnapshotMetric: {
    minWidth: 0,
    background: UI.cardSoft,
    border: `1px solid ${UI.border}`,
    borderRadius: 10,
    padding: "9px 6px",
    textAlign: "center",
  },
  mobileSnapshotFoot: {
    marginTop: 8,
    color: UI.textMuted,
    fontSize: 10.5,
    textAlign: "right",
  },
  sectionHeaderMobile: {
    alignItems: "flex-start",
    marginBottom: 9,
  },
  sectionTitleMobile: {
    fontSize: 18,
  },
  sectionDescMobile: {
    marginTop: 3,
    fontSize: 11.5,
    lineHeight: 1.45,
  },
  mobileFocusCard: {
    width: "100%",
    border: `1px solid ${UI.border}`,
    borderRadius: 15,
    background: UI.card,
    padding: 13,
    textAlign: "left",
    color: UI.text,
    cursor: "pointer",
    boxShadow: "0 2px 7px rgba(15,23,42,.045)",
  },
  mobileFocusTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
  },
  mobileFocusCorpLine: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
    fontSize: 12.5,
  },
  mobileFocusTitle: {
    marginTop: 10,
    fontSize: 14,
    lineHeight: 1.48,
    fontWeight: 900,
    wordBreak: "keep-all",
    overflowWrap: "anywhere",
  },
  mobileFocusMeta: {
    marginTop: 5,
    color: UI.textMuted,
    fontSize: 10.5,
    fontWeight: 700,
  },
  mobileFocusBottom: {
    display: "grid",
    gridTemplateColumns: "auto auto minmax(0, 1fr)",
    alignItems: "center",
    gap: 8,
    marginTop: 11,
    paddingTop: 9,
    borderTop: `1px solid ${UI.border}`,
    color: UI.textSub,
    fontSize: 11,
  },
  assetPanelMobile: {
    padding: 0,
    border: "none",
    background: "transparent",
    boxShadow: "none",
    borderRadius: 0,
  },
  assetHeaderMobile: {
    alignItems: "flex-end",
    marginBottom: 9,
    padding: "0 1px",
  },
  assetTitleMobile: {
    fontSize: 18,
  },
  assetHeaderSub: {
    marginTop: 3,
    color: UI.textMuted,
    fontSize: 10.5,
  },
  assetCountBadge: {
    flex: "0 0 auto",
    borderRadius: 999,
    background: UI.blueSoft,
    color: UI.blue,
    padding: "5px 9px",
    fontSize: 10.5,
    fontWeight: 900,
  },
  assetRowMobile: {
    borderRadius: 15,
    boxShadow: "0 2px 7px rgba(15,23,42,.04)",
  },
  assetRowMainMobile: {
    width: "100%",
    display: "block",
    border: "none",
    background: "transparent",
    padding: 13,
    textAlign: "left",
    color: UI.text,
    cursor: "pointer",
    boxSizing: "border-box",
  },
  mobileAssetTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 10,
  },
  mobileAssetIdentity: {
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  mobileAssetCorp: {
    minWidth: 0,
    color: UI.text,
    fontSize: 13,
    fontWeight: 900,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  mobileAssetScoreBox: {
    flex: "0 0 auto",
    minWidth: 44,
    textAlign: "right",
  },
  mobileAssetTitle: {
    marginTop: 10,
    color: UI.text,
    fontSize: 13.5,
    fontWeight: 900,
    lineHeight: 1.48,
    wordBreak: "keep-all",
    overflowWrap: "anywhere",
  },
  mobileAssetMetaRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
    marginTop: 9,
  },
  mobileMetaPill: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 25,
    borderRadius: 999,
    background: UI.cardSoft,
    border: `1px solid ${UI.border}`,
    color: UI.textSub,
    padding: "3px 8px",
    fontSize: 10.5,
    fontWeight: 800,
  },
  mobileAssetFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
    marginTop: 10,
    paddingTop: 9,
    borderTop: `1px solid ${UI.border}`,
    color: UI.textMuted,
    fontSize: 10.5,
    fontWeight: 800,
  },
  assetExpandedMobile: {
    padding: 10,
  },
  mobileAnalysisBlock: {
    background: UI.card,
    border: `1px solid ${UI.border}`,
    borderRadius: 11,
    padding: 11,
  },
  expandedActionRowMobile: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 7,
  },
  mobileFullButton: {
    width: "100%",
    height: 40,
    boxSizing: "border-box",
  },
  mobilePagination: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    gap: 8,
    marginTop: 16,
  },
  mobilePageNavButton: {
    height: 40,
    border: `1px solid ${UI.border}`,
    borderRadius: 11,
    background: UI.card,
    color: UI.text,
    fontSize: 11.5,
    fontWeight: 850,
    cursor: "pointer",
  },
  mobilePageIndicator: {
    display: "flex",
    alignItems: "baseline",
    gap: 3,
    minWidth: 54,
    justifyContent: "center",
    color: UI.textMuted,
    fontSize: 11,
  },
  drawerBackdropMobile: {
    zIndex: 19999,
  },
  drawerMobile: {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: "100vw",
    maxWidth: "100vw",
    height: "100dvh",
    minHeight: "100dvh",
    borderLeft: "none",
    boxShadow: "none",
    zIndex: 20000,
    overscrollBehavior: "contain",
  },
  drawerHeaderMobile: {
    position: "sticky",
    top: 0,
    zIndex: 3,
    padding: "calc(12px + env(safe-area-inset-top)) 14px 12px",
    background: "rgba(255,255,255,.98)",
    backdropFilter: "blur(12px)",
  },
  drawerMetaLine: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    flexWrap: "wrap",
    color: UI.textMuted,
    fontSize: 10.5,
    fontWeight: 800,
    marginBottom: 8,
  },
  drawerTitleMobile: {
    fontSize: 17,
    lineHeight: 1.4,
    paddingRight: 4,
    wordBreak: "keep-all",
    overflowWrap: "anywhere",
  },
  drawerSubMobile: {
    marginTop: 5,
    fontSize: 11.5,
  },
  closeButtonMobile: {
    width: 40,
    height: 40,
    minWidth: 40,
    borderRadius: 12,
    fontSize: 25,
  },
  drawerBodyMobile: {
    minWidth: 0,
    paddingBottom: 92,
  },
  drawerSectionMobile: {
    padding: "16px 14px",
  },
  drawerTextMobile: {
    fontSize: 13.5,
    lineHeight: 1.72,
    wordBreak: "keep-all",
    overflowWrap: "anywhere",
  },
  relatedAssetItemMobile: {
    padding: 11,
    gap: 10,
    minWidth: 0,
  },
  evidenceRow: {
    display: "grid",
    gridTemplateColumns: "76px minmax(0, 1fr)",
    alignItems: "start",
    gap: 10,
    padding: "9px 0",
    borderBottom: `1px solid ${UI.border}`,
  },
  evidenceLabel: {
    color: UI.textMuted,
    fontSize: 11.5,
    fontWeight: 900,
  },
  evidenceValue: {
    minWidth: 0,
    color: UI.text,
    fontSize: 12.5,
    lineHeight: 1.5,
    wordBreak: "keep-all",
    overflowWrap: "anywhere",
  },
  drawerActionBar: {
    position: "sticky",
    bottom: 0,
    zIndex: 4,
    display: "flex",
    gap: 8,
    padding: "12px 16px",
    background: "rgba(255,255,255,.98)",
    borderTop: `1px solid ${UI.border}`,
    backdropFilter: "blur(12px)",
  },
  drawerActionBarMobile: {
    padding: "10px 12px calc(10px + env(safe-area-inset-bottom))",
  },
  drawerPrimaryAction: {
    flex: "1 1 0",
    minWidth: 0,
  },
  drawerSecondaryAction: {
    flex: "0 0 84px",
    minWidth: 84,
  },
};

